/**
 * Badge - Unified badge component for status, labels, and tags
 *
 * Provides consistent badge styling across the app with:
 * - Semantic color variants (success, warning, danger, info, etc.)
 * - Size options (xs, sm, md)
 * - Optional icon support
 * - Uppercase and regular text styles
 */

import { type LucideIcon } from 'lucide-react';
import { CSSProperties, ReactNode } from 'react';

export type BadgeVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'accent';

export type BadgeSize = 'xs' | 'sm' | 'md';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: LucideIcon;
  uppercase?: boolean;
  pill?: boolean; // More rounded corners
  outlined?: boolean; // Outlined style
  style?: CSSProperties;
  className?: string;
}

// Enhanced badge colors using CSS variables for theming
const BADGE_COLORS: Record<BadgeVariant, { bg: string; text: string; border: string }> = {
  default: { bg: 'var(--gray-100)', text: 'var(--text-muted)', border: 'var(--gray-200)' },
  primary: { bg: 'var(--info-bg)', text: 'var(--info-dark)', border: 'var(--info-border)' },
  success: { bg: 'var(--success-bg)', text: 'var(--success-dark)', border: 'var(--success-border)' },
  warning: { bg: 'var(--warning-bg)', text: 'var(--warning-dark)', border: 'var(--warning-border)' },
  danger: { bg: 'var(--danger-bg)', text: 'var(--error-dark)', border: 'var(--danger-border)' },
  info: { bg: 'var(--info-bg)', text: 'var(--info-dark)', border: 'var(--info-border)' },
  accent: { bg: 'var(--accent-bg)', text: 'var(--accent-dark)', border: 'var(--accent-border)' },
};

// Fallback colors when CSS variables aren't available (matches brand theme)
const BADGE_COLORS_FALLBACK: Record<BadgeVariant, { bg: string; text: string; border: string }> = {
  default: { bg: '#f4f4f7', text: '#6b7280', border: '#e8e8ed' },
  primary: { bg: '#f5f3ff', text: '#6338C4', border: '#e9e3ff' },     // Purple
  success: { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },     // Orange
  warning: { bg: '#fefce8', text: '#ca8a04', border: '#fef08a' },     // Amber
  danger: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },      // Red
  info: { bg: '#f5f3ff', text: '#6338C4', border: '#e9e3ff' },        // Purple (same as primary)
  accent: { bg: '#f5f3ff', text: '#6338C4', border: '#e9e3ff' },      // Purple
};

const SIZE_STYLES: Record<BadgeSize, { padding: string; fontSize: string; iconSize: number; gap: string }> = {
  xs: { padding: '2px 6px', fontSize: '10px', iconSize: 10, gap: '3px' },
  sm: { padding: '3px 8px', fontSize: '11px', iconSize: 11, gap: '4px' },
  md: { padding: '4px 10px', fontSize: '12px', iconSize: 12, gap: '5px' },
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  icon: Icon,
  uppercase = true,
  pill = false,
  outlined = false,
  style,
  className,
}: BadgeProps) {
  // Use CSS variables with fallback
  const colors = BADGE_COLORS[variant];
  const fallback = BADGE_COLORS_FALLBACK[variant];
  const sizing = SIZE_STYLES[size];

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sizing.gap,
        padding: sizing.padding,
        fontSize: sizing.fontSize,
        fontWeight: 600,
        letterSpacing: uppercase ? '0.02em' : 'normal',
        borderRadius: pill ? '999px' : '6px',
        background: outlined ? 'transparent' : colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap',
        textTransform: uppercase ? 'uppercase' : 'none',
        lineHeight: 1.3,
        // Fallback values for when CSS variables aren't defined
        ['--badge-bg' as string]: fallback.bg,
        ['--badge-text' as string]: fallback.text,
        ['--badge-border' as string]: fallback.border,
        ...style,
      }}
    >
      {Icon && <Icon size={sizing.iconSize} />}
      {children}
    </span>
  );
}

// Pre-built badge variants for common use cases
Badge.Status = function StatusBadge({
  status,
  size = 'sm',
  ...props
}: Omit<BadgeProps, 'children' | 'variant'> & { status: 'new' | 'pending' | 'active' | 'completed' | 'draft' | 'published' | 'archived' }) {
  const config: Record<string, { label: string; variant: BadgeVariant }> = {
    new: { label: 'New', variant: 'accent' },
    pending: { label: 'Pending', variant: 'warning' },
    active: { label: 'Active', variant: 'success' },
    completed: { label: 'Completed', variant: 'success' },
    draft: { label: 'Draft', variant: 'default' },
    published: { label: 'Published', variant: 'primary' },
    archived: { label: 'Archived', variant: 'default' },
  };
  const { label, variant } = config[status] || config.draft;
  return <Badge variant={variant} size={size} {...props}>{label}</Badge>;
};

Badge.Count = function CountBadge({
  count,
  variant = 'default',
  size = 'xs',
  max = 99,
  ...props
}: Omit<BadgeProps, 'children'> & { count: number; max?: number }) {
  const displayCount = count > max ? `${max}+` : count.toString();
  return (
    <Badge variant={variant} size={size} pill {...props}>
      {displayCount}
    </Badge>
  );
};

Badge.Tag = function TagBadge({
  children,
  color,
  size = 'sm',
  ...props
}: Omit<BadgeProps, 'variant'> & { color?: string }) {
  return (
    <Badge
      variant="default"
      size={size}
      uppercase={false}
      style={{
        background: color ? `${color}15` : undefined,
        color: color || undefined,
        borderColor: color ? `${color}30` : undefined,
      }}
      {...props}
    >
      {children}
    </Badge>
  );
};

export default Badge;
